import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  MagnifyingGlassIcon,
  QuestionMarkCircleIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  PhoneIcon,
  EnvelopeIcon,
  ArrowLeftIcon,
  QrCodeIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  SparklesIcon,
  VideoCameraIcon,
  ClockIcon
} from '@heroicons/react/24/outline'
import restaurantLogo from '../assets/restaurant-logo.png'

const HelpCenter = () => {
  // Brand Colors matching LandingPage
  const BRAND_ORANGE = '#F59E0B' // Amber-500
  const BRAND_BLACK = '#1F2937' // Gray-800
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedFaq, setExpandedFaq] = useState(null)

  const categories = [
    {
      icon: QrCodeIcon,
      title: 'Getting Started',
      description: 'Setup your restaurant and create your first QR menu',
      articles: 12,
      color: 'orange'
    },
    {
      icon: BookOpenIcon,
      title: 'Menu Management',
      description: 'Add items, categories, and customize your menu',
      articles: 8,
      color: 'blue'
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: 'Order Management',
      description: 'Handle orders, notifications, and customer communication',
      articles: 15,
      color: 'green'
    },
    {
      icon: VideoCameraIcon,
      title: 'Payment & Billing',
      description: 'Setup payments, view transactions, and manage billing',
      articles: 6,
      color: 'purple'
    }
  ]

  const faqs = [
    {
      question: 'How do I create my first QR menu?',
      answer: 'After signing up, go to your dashboard and click "Create Menu". Add your restaurant details, upload your logo, and start adding menu items. Once complete, generate your QR code from the menu settings.'
    },
    {
      question: 'Can customers order without downloading an app?',
      answer: 'Yes! Customers simply scan the QR code with their phone camera and can browse and order directly from their mobile browser. No app download required.'
    },
    {
      question: 'How do I receive payments from customers?',
      answer: 'We integrate with multiple payment gateways including Cashfree, Razorpay, and others. Set up your payment gateway in Settings > Payments, and customers can pay directly through the platform.'
    },
    {
      question: 'Can I customize the look of my menu?',
      answer: 'Absolutely! You can customize colors, fonts, layout, add your logo, and even create custom themes to match your restaurant\'s branding.'
    },
    {
      question: 'How do I manage multiple restaurant locations?',
      answer: 'Our Professional and Enterprise plans support multiple locations. Each location can have its own menu, staff, and settings while being managed from a single dashboard.'
    },
    {
      question: 'What if I need help setting up?',
      answer: 'We offer free onboarding support for all new customers. You can also schedule a demo call or contact our support team for personalized assistance.'
    },
    {
      question: 'How do staff receive order notifications?',
      answer: 'Staff can receive notifications through the web dashboard, mobile app, email, or WhatsApp. You can configure notification preferences in the staff management section.'
    },
    {
      question: 'Can I export my sales data?',
      answer: 'Yes, you can export sales reports, order data, and analytics in various formats including CSV and PDF from your dashboard analytics section.'
    }
  ]

  const quickLinks = [
    { title: 'Setup Your First Menu', time: '5 min read' },
    { title: 'Configure Payment Gateway', time: '3 min read' },
    { title: 'Add Staff Members', time: '2 min read' },
    { title: 'Generate QR Codes', time: '1 min read' },
    { title: 'Customize Menu Design', time: '4 min read' },
    { title: 'View Analytics Dashboard', time: '3 min read' }
  ]

  const getColorClasses = (color) => {
    const colors = {
      orange: 'bg-orange-100 text-orange-600',
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      purple: 'bg-purple-100 text-purple-600'
    }
    return colors[color] || colors.orange
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="relative z-40 bg-white border-b-4 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <motion.div 
              className="relative"
              whileHover={{ y: -2 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <div className="bg-white rounded-2xl px-4 py-3 border-4 border-black shadow-[4px_4px_0_0_rgba(0,0,0,1)] hover:shadow-[6px_6px_0_0_rgba(0,0,0,1)] transition-all">
                <img src={restaurantLogo} alt="Ordyrr Restaurant" className="h-10 sm:h-12 w-auto" />
              </div>
            </motion.div>

            {/* Navigation */}
            <div className="flex items-center gap-3 sm:gap-6">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link 
                  to="/" 
                  className="px-6 py-2.5 sm:px-8 sm:py-3 rounded-full font-black text-sm sm:text-base transition-all shadow-[4px_4px_0_0_rgba(0,0,0,1)] hover:shadow-[6px_6px_0_0_rgba(0,0,0,1)] active:shadow-[2px_2px_0_0_rgba(0,0,0,1)] border-4 border-black bg-white text-black hover:bg-gray-50"
                >
                  ← BACK HOME
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
        <div className="h-1 bg-black"></div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden" style={{ backgroundColor: BRAND_ORANGE }}>
        {/* Playful Background Elements */}
        <div className="absolute inset-0">
          <motion.div 
            className="absolute top-20 right-10 w-24 h-24 rounded-full border-4 border-black/10"
            animate={{ y: [0, -20, 0], rotate: [0, 180, 360] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute bottom-32 left-16 w-16 h-16 rounded-full bg-black/5"
            animate={{ y: [0, 20, 0], scale: [1, 1.2, 1] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="relative z-10 px-4 py-12 sm:py-16 lg:py-24">
          <div className="max-w-6xl mx-auto text-center">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="inline-flex items-center gap-2 bg-black px-4 py-2 rounded-full border-2 border-black shadow-[3px_3px_0_0_rgba(0,0,0,0.3)] mb-8"
            >
              <SparklesIcon className="w-5 h-5 text-amber-400" />
              <span className="font-black text-sm tracking-wide text-amber-400">HELP CENTER</span>
            </motion.div>

            {/* Main Headline */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="space-y-1 sm:space-y-2 mb-8"
            >
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-black leading-[0.9] tracking-tighter">
                HOW CAN WE
              </h1>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-black leading-[0.9] tracking-tighter">
                HELP YOU?
              </h1>
            </motion.div>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-base sm:text-lg lg:text-2xl font-black text-black/90 max-w-3xl mx-auto mb-8"
            >
              Find answers to common questions, browse our documentation, or get in touch with our support team.
            </motion.p>
            
            {/* Search Bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="relative max-w-2xl mx-auto"
            >
              <MagnifyingGlassIcon className="absolute left-6 top-1/2 transform -translate-y-1/2 h-6 w-6 text-black/60" />
              <input
                type="text"
                placeholder="Search for help articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-16 pr-6 py-4 border-4 border-black rounded-2xl focus:outline-none text-lg font-bold bg-white shadow-[4px_4px_0_0_rgba(0,0,0,1)] focus:shadow-[6px_6px_0_0_rgba(0,0,0,1)] transition-all"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Help Categories */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12 lg:mb-16"
          >
            <div className="inline-flex items-center gap-2 bg-black px-4 py-2 rounded-full border-2 border-black shadow-[3px_3px_0_0_rgba(0,0,0,0.3)] mb-6">
              <BookOpenIcon className="w-5 h-5 text-amber-400" />
              <span className="font-black text-sm tracking-wide text-amber-400">HELP CATEGORIES</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-black mb-4 leading-tight">
              BROWSE BY CATEGORY
            </h2>
            <p className="text-base sm:text-lg lg:text-xl font-bold text-black/70 max-w-2xl mx-auto">
              Find answers quickly by exploring our comprehensive help categories
            </p>
          </motion.div>

          {/* Categories Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {categories.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -8, scale: 1.03 }}
                className="group bg-white rounded-2xl p-6 sm:p-8 border-4 border-black shadow-[4px_4px_0_0_rgba(0,0,0,1)] hover:shadow-[8px_8px_0_0_rgba(0,0,0,1)] transition-all duration-300"
              >
                {/* Icon */}
                <motion.div 
                  className="flex justify-center mb-6"
                  whileHover={{ rotate: [0, -10, 10, 0] }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="p-4 rounded-2xl border-4 border-black shadow-[3px_3px_0_0_rgba(0,0,0,1)] group-hover:shadow-[5px_5px_0_0_rgba(0,0,0,1)] transition-all" style={{ backgroundColor: BRAND_ORANGE }}>
                    <category.icon className="h-8 w-8 text-black" />
                  </div>
                </motion.div>
                
                {/* Content */}
                <div className="text-center">
                  <h3 className="text-xl sm:text-2xl font-black text-black mb-3 group-hover:text-black transition-colors">
                    {category.title}
                  </h3>
                  <p className="text-sm sm:text-base text-black/70 font-bold mb-6 leading-relaxed">
                    {category.description}
                  </p>
                  
                  {/* Footer */}
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <span className="text-xs sm:text-sm font-black text-black/60 bg-gray-100 px-3 py-1 rounded-full border-2 border-black">
                      {category.articles} articles
                    </span>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <button className="bg-black text-white px-4 sm:px-6 py-2 rounded-full font-black text-sm border-4 border-black shadow-[3px_3px_0_0_rgba(0,0,0,1)] hover:shadow-[5px_5px_0_0_rgba(0,0,0,1)] transition-all w-full sm:w-auto">
                        BROWSE
                      </button>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-16 lg:py-20" style={{ backgroundColor: BRAND_ORANGE }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12 lg:mb-16"
          >
            <div className="inline-flex items-center gap-2 bg-black px-4 py-2 rounded-full border-2 border-black shadow-[3px_3px_0_0_rgba(0,0,0,0.3)] mb-6">
              <QuestionMarkCircleIcon className="w-5 h-5 text-amber-400" />
              <span className="font-black text-sm tracking-wide text-amber-400">FAQ SECTION</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-black mb-4 leading-tight">
              FREQUENTLY ASKED
            </h2>
            <p className="text-base sm:text-lg lg:text-xl font-bold text-black/70 max-w-2xl mx-auto">
              Quick answers to common questions about our platform
            </p>
          </motion.div>

          {/* FAQ Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-white rounded-2xl border-4 border-black shadow-[4px_4px_0_0_rgba(0,0,0,1)] hover:shadow-[6px_6px_0_0_rgba(0,0,0,1)] transition-all duration-300 overflow-hidden"
              >
                <motion.button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full px-6 sm:px-8 py-6 text-left flex items-start sm:items-center justify-between hover:bg-gray-50 transition-colors group"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <span className="font-black text-black text-base sm:text-lg pr-4 leading-tight group-hover:text-gray-800 transition-colors">
                    {faq.question}
                  </span>
                  <motion.div 
                    className="flex-shrink-0 p-2 rounded-full border-2 border-black transition-all" 
                    style={{ backgroundColor: expandedFaq === index ? BRAND_ORANGE : 'white' }}
                    whileHover={{ rotate: expandedFaq === index ? 180 : 90 }}
                    transition={{ duration: 0.2 }}
                  >
                    {expandedFaq === index ? (
                      <ChevronDownIcon className="h-4 w-4 sm:h-5 sm:w-5 text-black" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4 sm:h-5 sm:w-5 text-black" />
                    )}
                  </motion.div>
                </motion.button>
                
                {expandedFaq === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                    className="px-6 sm:px-8 pb-6 border-t-4 border-black" 
                    style={{ backgroundColor: BRAND_ORANGE }}
                  >
                    <p className="text-black font-bold text-sm sm:text-base leading-relaxed pt-4">
                      {faq.answer}
                    </p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Support */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            {/* Support Card */}
            <div className="rounded-3xl p-8 sm:p-12 lg:p-16 border-4 border-black shadow-[8px_8px_0_0_rgba(0,0,0,1)] relative overflow-hidden" style={{ backgroundColor: BRAND_ORANGE }}>
              {/* Background Elements */}
              <div className="absolute inset-0 opacity-10">
                <motion.div 
                  className="absolute top-10 right-10 w-20 h-20 rounded-full border-4 border-black"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                />
                <motion.div 
                  className="absolute bottom-10 left-10 w-16 h-16 rounded-full bg-black"
                  animate={{ y: [0, -20, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                />
              </div>
              
              {/* Content */}
              <div className="relative z-10">
                {/* Badge */}
                <div className="inline-flex items-center gap-2 bg-black px-4 py-2 rounded-full border-2 border-black shadow-[3px_3px_0_0_rgba(0,0,0,0.3)] mb-6">
                  <ChatBubbleLeftRightIcon className="w-5 h-5 text-amber-400" />
                  <span className="font-black text-sm tracking-wide text-amber-400">SUPPORT TEAM</span>
                </div>
                
                <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-black mb-6 leading-tight">
                  STILL NEED HELP?
                </h2>
                <p className="text-base sm:text-lg lg:text-xl font-bold text-black/80 mb-10 max-w-3xl mx-auto leading-relaxed">
                  Our dedicated support team is here 24/7 to help you succeed. Get personalized assistance and expert guidance.
                </p>
                
                {/* Support Options */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 max-w-4xl mx-auto mb-10">
                  {/* Contact Support */}
                  <motion.div whileHover={{ scale: 1.05, y: -5 }} whileTap={{ scale: 0.95 }}>
                    <Link
                      to="/contact"
                      className="group bg-black text-white p-6 rounded-2xl font-black border-4 border-black shadow-[4px_4px_0_0_rgba(0,0,0,1)] hover:shadow-[6px_6px_0_0_rgba(0,0,0,1)] transition-all flex flex-col items-center text-center"
                    >
                      <div className="p-3 rounded-full mb-4 group-hover:bg-gray-800 transition-colors" style={{ backgroundColor: BRAND_ORANGE }}>
                        <EnvelopeIcon className="h-6 w-6 text-black" />
                      </div>
                      <span className="text-sm sm:text-base">CONTACT SUPPORT</span>
                      <span className="text-xs text-gray-300 mt-1">Get help via email</span>
                    </Link>
                  </motion.div>
                  
                  {/* Live Chat */}
                  <motion.div whileHover={{ scale: 1.05, y: -5 }} whileTap={{ scale: 0.95 }}>
                    <button className="group bg-white text-black p-6 rounded-2xl font-black border-4 border-black shadow-[4px_4px_0_0_rgba(0,0,0,1)] hover:shadow-[6px_6px_0_0_rgba(0,0,0,1)] transition-all flex flex-col items-center text-center w-full">
                      <div className="p-3 rounded-full mb-4 bg-black group-hover:bg-gray-800 transition-colors">
                        <ChatBubbleLeftRightIcon className="h-6 w-6 text-white" />
                      </div>
                      <span className="text-sm sm:text-base">💬 LIVE CHAT</span>
                      <span className="text-xs text-gray-600 mt-1">Instant messaging</span>
                    </button>
                  </motion.div>
                  
                  {/* Phone Support */}
                  <motion.div whileHover={{ scale: 1.05, y: -5 }} whileTap={{ scale: 0.95 }} className="sm:col-span-2 lg:col-span-1">
                    <button className="group bg-white text-black p-6 rounded-2xl font-black border-4 border-black shadow-[4px_4px_0_0_rgba(0,0,0,1)] hover:shadow-[6px_6px_0_0_rgba(0,0,0,1)] transition-all flex flex-col items-center text-center w-full">
                      <div className="p-3 rounded-full mb-4" style={{ backgroundColor: BRAND_ORANGE }}>
                        <PhoneIcon className="h-6 w-6 text-black" />
                      </div>
                      <span className="text-sm sm:text-base">📞 CALL US</span>
                      <span className="text-xs text-gray-600 mt-1">Direct phone support</span>
                    </button>
                  </motion.div>
                </div>
                
                {/* Response Time */}
                <div className="inline-flex items-center gap-2 bg-black/10 px-4 py-2 rounded-full border-2 border-black">
                  <ClockIcon className="w-4 h-4 text-black" />
                  <span className="font-black text-sm text-black">Average response time: 2 minutes</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default HelpCenter
